import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { OnboardingProvider } from "@/context/OnboardingContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Library from "@/pages/Library";
import Reader from "@/pages/Reader";
import Workspaces from "@/pages/Workspaces";
import Workspace from "@/pages/Workspace";
import Analytics from "@/pages/Analytics";
import Profile from "@/pages/Profile";
import Learn from "@/pages/Learn";
import Glossary from "@/pages/Glossary";
import Study from "@/pages/Study";
import Review from "@/pages/Review";
import Exam from "@/pages/Exam";
import StudyPicker from "@/pages/StudyPicker";
import ExamPicker from "@/pages/ExamPicker";
import Pricing from "@/pages/Pricing";
import Ambassador from "@/pages/Ambassador";
import Admin from "@/pages/Admin";

const FullScreenLoader = () => (
    <div className="flex min-h-screen items-center justify-center bg-surface-0">
        <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" />
    </div>
);

const Protected = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return <FullScreenLoader />;
    if (!user) return <Navigate to="/login" replace />;
    return children;
};

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route element={<Protected><AppLayout /></Protected>}>
                <Route path="/library" element={<Library />} />
                <Route path="/workspaces" element={<Workspaces />} />
                <Route path="/workspaces/:id" element={<Workspace />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/learn" element={<Learn />} />
                <Route path="/glossary" element={<Glossary />} />
                <Route path="/study" element={<StudyPicker />} />
                <Route path="/exam" element={<ExamPicker />} />
                <Route path="/ambassador" element={<Ambassador />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/profile" element={<Profile />} />
            </Route>
            <Route path="/read/:id" element={<Protected><Reader /></Protected>} />
            <Route path="/study/:id" element={<Protected><Study /></Protected>} />
            <Route path="/exam/:id" element={<Protected><Exam /></Protected>} />
            <Route path="/review" element={<Protected><Review /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App dark">
            <LanguageProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <OnboardingProvider>
                            <AppRoutes />
                        </OnboardingProvider>
                        <Toaster position="bottom-right" theme="dark" richColors />
                    </BrowserRouter>
                </AuthProvider>
            </LanguageProvider>
        </div>
    );
}

export default App;
